'use client'

import { cn } from '@/lib/tailwind'
import {
  CaretDownIcon,
  XIcon,
  TagIcon,
  MagnifyingGlassIcon,
  PlusIcon,
} from '@phosphor-icons/react'
import { Popover, PopoverContent, PopoverTrigger } from '@/ui/popover'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/ui/dialog'
import { Input } from '@/ui/input'
import { Button } from '@/ui/button'
import { useGroupedTopics } from './use-grouped-topics'
import { useFindOrCreateTopic, useTopics } from './use-topics'
import type { Topic, EntryType } from '@/document'
import { useState, useMemo } from 'react'

interface TopicFilterProps {
  selected: string[] // Topic IDs
  onChange: (selected: string[]) => void
  className?: string
  entryType?: EntryType | null // Default entry type for topic creation
}

export function TopicFilter({
  selected,
  onChange,
  className,
  entryType,
}: TopicFilterProps) {
  // Use filtered topics when entryType is provided, grouped otherwise
  const filteredResult = useTopics(entryType)
  const groupedResult = useGroupedTopics()
  const findOrCreateTopic = useFindOrCreateTopic()

  const isLoading =
    entryType !== undefined ? filteredResult.isLoading : groupedResult.isLoading
  const hasSelection = selected.length > 0
  const [search, setSearch] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [newTopicName, setNewTopicName] = useState('')
  const [selectedEntryType, setSelectedEntryType] = useState<EntryType | null>(
    entryType ?? null,
  )

  const toggleTopic = (id: string) => {
    if (selected.includes(id)) {
      onChange(selected.filter((s) => s !== id))
    } else {
      onChange([...selected, id])
    }
  }

  const handleCreateTopic = () => {
    if (!newTopicName.trim() || !selectedEntryType) return

    // Create the topic
    const topicId = findOrCreateTopic(newTopicName.trim(), selectedEntryType)

    if (topicId) {
      // Auto-select the newly created topic
      onChange([...selected, topicId])
    }

    // Close dialog and reset
    setShowCreateDialog(false)
    setNewTopicName('')
    setSelectedEntryType(entryType ?? null)

    // Close the popover as well
    setIsOpen(false)
  }

  const openCreateDialog = () => {
    setNewTopicName(search.trim())
    setShowCreateDialog(true)
  }

  // Filter topics based on search query
  const filteredData = useMemo(() => {
    if (entryType !== undefined) {
      // Flat list mode (when entryType is provided)
      const topics = filteredResult.topics
      if (!search.trim()) return topics

      return topics.filter((topic) =>
        topic.name.toLowerCase().includes(search.toLowerCase()),
      )
    } else {
      // Grouped mode (when entryType is NOT provided)
      const grouped = groupedResult.grouped
      if (!search.trim()) return grouped

      const filterTopics = (topics: Topic[]) =>
        topics.filter((topic) =>
          topic.name.toLowerCase().includes(search.toLowerCase()),
        )

      return {
        journal: filterTopics(grouped.journal),
        task: filterTopics(grouped.task),
        other: filterTopics(grouped.other),
      }
    }
  }, [entryType, filteredResult.topics, groupedResult.grouped, search])

  // Check if search has no results
  const hasNoResults =
    search.trim() &&
    (entryType !== undefined
      ? Array.isArray(filteredData) && filteredData.length === 0
      : !Array.isArray(filteredData) &&
        filteredData.journal.length === 0 &&
        filteredData.task.length === 0 &&
        filteredData.other.length === 0)

  // Clear search when popover closes
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (!open) {
      setSearch('')
    }
  }

  // Render a section with label
  const renderSection = (label: string, topics: Topic[]) => {
    if (topics.length === 0) return null

    return (
      <div key={label}>
        <p className="text-muted-foreground mt-2 mb-1.5 text-[10px] font-medium tracking-wide uppercase first:mt-0">
          {label}
        </p>
        <div className="space-y-0.5">
          {topics.map((topic) => {
            const isSelected = selected.includes(topic.id)
            return (
              <button
                key={topic.id}
                onClick={() => toggleTopic(topic.id)}
                className={cn(
                  'flex w-full items-center rounded px-2 py-1.5 text-left text-sm transition-colors',
                  isSelected
                    ? 'bg-foreground/10 text-foreground font-medium'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent/50',
                )}
              >
                {topic.name}
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <>
      <Popover open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            className={cn(
              'inline-flex min-h-[44px] items-center gap-1 rounded px-3 py-2 text-sm font-medium transition-colors active:scale-[0.98] sm:min-h-auto sm:px-2 sm:py-1',
              hasSelection
                ? 'bg-foreground/10 text-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-accent/50',
              className,
            )}
          >
            <TagIcon className="h-4 w-4 sm:h-3 sm:w-3" />
            {hasSelection ? `Topics (${selected.length})` : 'Topics'}
            <CaretDownIcon className="h-3 w-3 opacity-60" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-2" align="start">
          {isLoading ? (
            <p className="text-muted-foreground text-xs">Loading...</p>
          ) : (
            <>
              {/* Search input */}
              <div className="relative mb-2">
                <MagnifyingGlassIcon className="text-muted-foreground pointer-events-none absolute top-1/2 left-2 h-3 w-3 -translate-y-1/2" />
                <Input
                  type="text"
                  placeholder="Search topics..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-8 pr-2 pl-7 text-sm"
                />
              </div>

              {/* Topic list */}
              <div className="max-h-64 overflow-y-auto">
                {hasNoResults ? (
                  <div className="py-4 text-center">
                    <p className="text-muted-foreground mb-3 text-sm">
                      No topics found
                    </p>
                    <button
                      onClick={openCreateDialog}
                      className="text-foreground hover:bg-accent/50 mx-auto flex items-center gap-1.5 rounded px-3 py-2 text-sm font-medium transition-colors"
                    >
                      <PlusIcon className="h-4 w-4" />
                      Create &quot;{search.trim()}&quot;
                    </button>
                  </div>
                ) : entryType !== undefined ? (
                  // Flat list mode (entryType provided)
                  <div className="space-y-0.5">
                    {Array.isArray(filteredData) &&
                      filteredData.map((topic) => {
                        const isSelected = selected.includes(topic.id)
                        return (
                          <button
                            key={topic.id}
                            onClick={() => toggleTopic(topic.id)}
                            className={cn(
                              'flex w-full items-center rounded px-2 py-1.5 text-left text-sm transition-colors',
                              isSelected
                                ? 'bg-foreground/10 text-foreground font-medium'
                                : 'text-muted-foreground hover:text-foreground hover:bg-accent/50',
                            )}
                          >
                            {topic.name}
                          </button>
                        )
                      })}
                  </div>
                ) : (
                  // Grouped mode (entryType NOT provided)
                  <>
                    {!Array.isArray(filteredData) && (
                      <>
                        {renderSection('Journal Topics', filteredData.journal)}
                        {renderSection('Task Topics', filteredData.task)}
                        {renderSection('Other', filteredData.other)}
                      </>
                    )}
                  </>
                )}

                {hasSelection && (
                  <>
                    <div className="bg-border my-2 h-px" />
                    <button
                      onClick={() => onChange([])}
                      className="text-muted-foreground hover:text-foreground flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-left text-sm transition-colors"
                    >
                      <XIcon className="h-3 w-3" />
                      Clear all
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </PopoverContent>
      </Popover>

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Topic</DialogTitle>
            <DialogDescription>
              Add a new topic to organize your entries.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-foreground mb-2 block text-sm font-medium">
                Topic Name
              </label>
              <Input
                type="text"
                placeholder="e.g., Machine Learning"
                value={newTopicName}
                onChange={(e) => setNewTopicName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    e.stopPropagation()
                    handleCreateTopic()
                  }
                }}
              />
            </div>

            <div>
              <label className="text-foreground mb-2 block text-sm font-medium">
                Entry Type
              </label>
              <select
                value={selectedEntryType ?? ''}
                onChange={(e) =>
                  setSelectedEntryType(
                    e.target.value ? (e.target.value as EntryType) : null,
                  )
                }
                className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="">Select entry type</option>
                <option value="journal">Journal</option>
                <option value="task">Task</option>
                <option value="book">Book</option>
                <option value="movie">Movie</option>
                <option value="artist">Artist</option>
                <option value="release">Album/Release</option>
                <option value="label">Label</option>
              </select>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateTopic}
              disabled={!newTopicName.trim() || !selectedEntryType}
            >
              Create Topic
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
